<template>
  <div class="revenue-expense-chart">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <div class="chart-header">
        <h6 class="chart-title">{{ $t('financialStats.financialOverview') }}</h6>
        <div class="chart-subtitle">{{ $t('financialStats.revenueVsExpenses') }}</div>
      </div>
      
      <apexchart
        :options="chartOptions"
        :series="chartData.series"
        type="bar"
        height="300"
        class="chart-container"
      />
    </div>
  </div>
</template>

<script>
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "RevenueExpenseChart",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({
        revenue: 0,
        expenses: 0,
        profit: 0
      })
    }
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  computed: {
    chartData() {
      const revenue = this.data.revenue || 0;
      const expenses = this.data.expenses || 0;
      const profit = this.data.profit || 0;
      
      // Normalize values for better visualization
      const maxValue = Math.max(revenue, expenses, Math.abs(profit));
      const normalizedRevenue = maxValue > 0 ? (revenue / maxValue) * 100 : 0;
      const normalizedExpenses = maxValue > 0 ? (expenses / maxValue) * 100 : 0;
      const normalizedProfit = maxValue > 0 ? (Math.abs(profit) / maxValue) * 100 : 0;
      
      return {
        series: [
          { name: this.$t('financialStats.revenue'), data: [normalizedRevenue] },
          { name: this.$t('financialStats.expenses'), data: [normalizedExpenses] },
          { name: this.$t('financialStats.profit'), data: [normalizedProfit] }
        ]
      };
    },
    
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          height: 300,
          toolbar: {
            show: false
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        colors: ['#52c41a', '#ff4d4f', '#1890ff'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 4,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val, { seriesIndex }) => {
            const values = [this.data.revenue, this.data.expenses, this.data.profit];
            return values[seriesIndex] ? values[seriesIndex].toLocaleString() + ` ${this.currency}` : '';
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#333']
          }
        },
        xaxis: {
          categories: [this.$t('financialStats.revenue'), this.$t('financialStats.expenses'), this.$t('financialStats.profit')],
          labels: {
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return value + '%';
            },
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value, { seriesIndex }) => {
              const values = [this.data.revenue, this.data.expenses, this.data.profit];
              return values[seriesIndex] ? values[seriesIndex].toLocaleString() + ` ${this.currency}` : '';
            }
          }
        },
        legend: {
          show: false
        },
        grid: {
          borderColor: '#f0f0f0',
          strokeDashArray: 3
        }
      };
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) {
        return `0 ${this.currency}`;
      }
      return Number(value).toLocaleString() + ` ${this.currency}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';

</style>